/**
 * Composant de fallback affiché quand Sentry.ErrorBoundary attrape
 * une erreur React non gérée.
 *
 * Affiche un message clair, propose de réessayer (reset du composant
 * qui a planté) ou de recharger la page complète. En dev, affiche aussi
 * les détails techniques de l'erreur pour faciliter le debug.
 *
 * Le branding reprend les couleurs Centralis Oger (orange/ambre) pour
 * être cohérent avec le reste de l'app.
 */
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export default function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const isDev = import.meta.env.DEV;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: '"Inter", sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '520px',
          width: '100%',
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 20px 50px -10px rgba(0, 0, 0, 0.1)',
          padding: '40px 36px',
          textAlign: 'center',
          border: '1px solid #f1f5f9',
        }}
      >
        {/* Icône */}
        <div
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            border: '2px solid #fed7aa',
          }}
        >
          <AlertTriangle size={36} color="#fb803c" strokeWidth={2} />
        </div>

        {/* Titre */}
        <h1
          style={{
            margin: '0 0 12px',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#0b0802',
            letterSpacing: '-0.01em',
          }}
        >
          Oups, quelque chose s'est mal passé
        </h1>

        {/* Message */}
        <p
          style={{
            margin: '0 0 28px',
            fontSize: '0.95rem',
            color: '#64748b',
            lineHeight: 1.6,
          }}
        >
          Une erreur inattendue est survenue. Tu peux essayer de rafraîchir
          l'écran ou recharger la page complète. Si le problème persiste,
          contacte l'administrateur.
        </p>

        {/* Détails technique en dev uniquement */}
        {isDev && error && (
          <details
            style={{
              textAlign: 'left',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '8px',
              padding: '12px 14px',
              marginBottom: '24px',
              fontSize: '0.8rem',
              color: '#78350f',
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                fontWeight: 600,
                marginBottom: '8px',
              }}
            >
              Détails techniques (dev uniquement)
            </summary>
            <div style={{ marginTop: '8px' }}>
              <div style={{ marginBottom: '6px' }}>
                <strong>{error.name}</strong>: {error.message}
              </div>
              {error.stack && (
                <pre
                  style={{
                    margin: 0,
                    padding: '8px',
                    background: 'rgba(0,0,0,0.05)',
                    borderRadius: '4px',
                    fontSize: '0.72rem',
                    overflow: 'auto',
                    maxHeight: '160px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {error.stack}
                </pre>
              )}
            </div>
          </details>
        )}

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={resetError}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '11px 22px',
              borderRadius: '10px',
              border: '1px solid #fed7aa',
              background: 'linear-gradient(135deg, #fb803c 0%, #f19c59 100%)',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 12px -2px rgba(251, 128, 60, 0.3)',
            }}
          >
            <RotateCcw size={16} />
            Réessayer
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '11px 22px',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              background: 'white',
              color: '#334155',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} />
            Recharger la page
          </button>
        </div>
      </div>
    </div>
  );
}
